'use client';

import {
  createLocalStorageWithTTL,
  TTL,
} from '@/utils/storage/localStorage-with-ttl';
import { getPromotionAttribution } from './promotion-attribution';

// Spec §5.3.7: the list a product was chosen from is remembered for 24 hours so
// it can be attached at item level to `view_item`, `add_to_cart`,
// `begin_checkout` and `purchase`. Same pattern as `promotion-attribution.js`,
// but keyed per `item_id` rather than one value for the session.
const STORAGE_KEY = 'gtm_item_list_attribution';

const storage = createLocalStorageWithTTL(
  'gtm-item-list-attribution',
  TTL.ONE_DAY
);

// The adapter's TTL covers the whole blob and is refreshed on every write, so a
// product clicked today would keep yesterday's entries alive. Each entry
// therefore carries its own timestamp and is filtered on read.
const ENTRY_TTL_MS = TTL.ONE_DAY;

// Bounds the blob. A shopper who browses all day should not grow it without
// limit, and the oldest entries are the least useful.
const MAX_ENTRIES = 50;

const readAll = () => storage.getItem(STORAGE_KEY) || {};

const isFresh = (entry) =>
  Boolean(entry?.t) && Date.now() - entry.t < ENTRY_TTL_MS;

const prune = (entries) =>
  Object.fromEntries(
    Object.entries(entries)
      .filter(([, entry]) => isFresh(entry))
      .sort(([, a], [, b]) => b.t - a.t)
      .slice(0, MAX_ENTRIES)
  );

export const storeItemListAttribution = (item) => {
  if (!item?.item_id || !item?.item_list_id) return;

  const entries = readAll();
  entries[item.item_id] = {
    item_list_id: item.item_list_id,
    ...(item.item_list_name && { item_list_name: item.item_list_name }),
    // only items picked on the promotion's landing page carry it.
    ...getPromotionAttribution(),
    t: Date.now(),
  };

  // Pruned after inserting, so the cap counts the entry just written. It has
  // the newest timestamp, so it is never the one dropped.
  storage.setItem(STORAGE_KEY, prune(entries));
};

// Returns `{ item_list_id, item_list_name? }` or undefined once the entry is
// older than 24 hours. `t` is internal and never leaves this module.
export const getItemListAttribution = (itemId) => {
  const entry = readAll()?.[itemId];
  if (!isFresh(entry)) return undefined;

  return {
    item_list_id: entry.item_list_id,
    ...(entry.item_list_name && { item_list_name: entry.item_list_name }),
    ...(entry.promotion_id && { promotion_id: entry.promotion_id }),
    ...(entry.promotion_name && { promotion_name: entry.promotion_name }),
  };
};

export const clearItemListAttribution = () => storage.removeItem(STORAGE_KEY);

export const getItemAttribution = (itemId) => getItemListAttribution(itemId);
